import React from 'react';
import {
  AbsoluteFill, Img, Loop, OffthreadVideo, Sequence, staticFile, spring, interpolate, Easing,
  useCurrentFrame, useVideoConfig, delayRender, continueRender,
} from 'remotion';
import {loadFont as loadDeva} from '@remotion/google-fonts/NotoSansDevanagari';
import {loadFont as loadInter} from '@remotion/google-fonts/Inter';
import {GROUNDS, PLATES, TEXTS, LABELS, MARKS, WORDS, GROUND_LOOP, ALPHA_META, Plate, Text, Label, Layer} from './timeline';
import {CUSTOM} from './custom';

/**
 * SANATAN DECODE — the episode master comp (skill `sanatan-decode-episode`, installed by `episode.py comp`).
 * "lit ground + flat evidence + H3 film": every layer by engine —
 *   GROUND  = H3 fl2va lit room per act, pre-baked blurred+dimmed ping-pong loop, STATIC while resident, 0.5 s push across the act cut
 *   ALPHA   = gpt-image-2 plate keyed on chroma green, split into ELEMENT LAYERS that arrive (swing) / sharpen (blur) / count in (chain) on their spoken word
 *   GRID    = N panels, all blurred + dimmed, each sharpens on its word, spoken ones settle to 80 %
 *   FILM    = full-frame H3 clip, hard cut, its length IS the narration window (rounded up to the 17n+5 rung)
 *   TEXT    = captions / headings / labels set by Remotion on the WTS word — never a word that is already baked into the plate
 * LAWS (sanatan-decode-motion §1, §8b, §8d): ON TWOS · nothing fades, everything swings (damping 8, pivot above) or is not mounted ·
 * ONE accent (amber) · one instance per object · text in flow layout · every visual element carries data-audit.
 * This file is GENERIC — it reads ./timeline (generated) and ./custom (episode-specific hand-authored beats). Do not hand-edit times here.
 */

const {fontFamily: devaBase} = loadDeva('normal', {weights: ['400', '600', '700', '800'], subsets: ['devanagari', 'latin']});
const {fontFamily: interBase} = loadInter('normal', {weights: ['500', '700'], subsets: ['latin']});
export const deva = `${devaBase}, ${interBase}, sans-serif`;

export const CREAM = '#F2E9D8';
export const TEXT = '#FFF6E5';                 // Remotion type is brighter than the plate cream — readable on the dark ground (Yash, 2026-09-06)
export const AMBER = '#F0913A';
export const SCRIM = 'rgba(12, 9, 6, 0.74)';
const TEXT_SHADOW = '0 2px 8px rgba(0,0,0,0.65)';

export const useStep = () => Math.floor(useCurrentFrame() / 2) * 2;
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
export const wf = (i: number, fps: number) => Math.round(WORDS[i][0] * fps);
export const swingIn = (f: number, at: number, fps: number) =>
  spring({frame: Math.max(0, f - at), fps, config: {damping: 8, stiffness: 95, mass: 1.15}});
export const popIn = (f: number, at: number, fps: number) =>
  spring({frame: Math.max(0, f - at), fps, config: {damping: 9, stiffness: 150, mass: 0.8}});
const exitP = (f: number, out: number, len = 10) =>
  interpolate(clamp01((f - (out - len)) / len), [0, 1], [0, 1], {easing: Easing.in(Easing.cubic)});

/* ── GROUND ── */
const Grounds: React.FC<{f: number}> = ({f}) => (
  <>
    {GROUNDS.map((g) => {
      if (f < g.inFrame - 2 || f >= g.outFrame) return null;
      const toCut = g.outFrame - f;
      const push = toCut <= 12 ? 1 + 0.06 * (1 - toCut / 12) : 1;
      const loop = GROUND_LOOP[g.id] ?? 384;
      return (
        <div key={g.id} data-audit={`ground:${g.id}`} style={{position: 'absolute', inset: 0, transform: `scale(${push})`, transformOrigin: '50% 50%'}}>
          <Sequence from={g.inFrame} durationInFrames={g.outFrame - g.inFrame} layout="none">
            <Loop durationInFrames={loop} layout="none">
              <OffthreadVideo src={staticFile(g.asset)} muted style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
            </Loop>
          </Sequence>
        </div>
      );
    })}
  </>
);

/* ── the swing: pivot above, underdamped so it rings; exit = up and off in the last 10 frames ── */
export const Swing: React.FC<{f: number; inAt: number; out: number; fps: number; children: React.ReactNode; idle?: boolean; exit?: boolean; pop?: boolean}> =
  ({f, inAt, out, fps, children, idle = true, exit = true, pop = false}) => {
    const s = pop ? popIn(f, inAt, fps) : swingIn(f, inAt, fps);
    const e = exit ? exitP(f, out) : 0;
    const pend = idle ? 0.35 * Math.sin(((f - inAt) / (fps * 3.1)) * Math.PI * 2) : 0;
    const ty = (1 - s) * (pop ? -60 : -160) - e * 1400;
    const rot = (1 - s) * (pop ? -3 : -9) + pend + e * 7;
    return (
      <div style={{position: 'absolute', inset: 0, transform: `translateY(${ty}px) rotate(${rot}deg) scale(${pop ? 0.8 + 0.2 * s : 1})`, transformOrigin: '50% -28%'}}>
        {children}
      </div>
    );
  };

/* ── ALPHA PLATE ──
   fit: the art's measured bottom lands at 0.84 of the frame (0.76 when a label row exists; the caption band starts at 0.855) — position comes from measurement, never from the prompt.
   backing 'card': the plate's opaque pixels are mostly dark (dark_share > 0.55) → a bone-cream card sits behind the art so navy lettering reads.
   layers: element layers from episode.py post (zone assignment). mode per layer:
     swing — not mounted until its word, then swings in (the v20 plate motion, per element)
     blur  — mounted with the plate, blurred + dimmed until its word, sharpens on the word, settles to 80 % after the next element speaks
     chain — a counted piece (7 rungs, 5 rungs…) pops in at its own staggered frame */
const PUSH = 0.05;
const REST = {blur: 14, br: 0.72, sc: 1.0};
const ACTIVE = {blur: 0, br: 1.0, sc: 1.04};
const SPOKEN = {blur: 0, br: 1.0, sc: 1.0};
const lerpS = (a: typeof REST, b: typeof REST, s: number) => ({
  blur: Math.max(0, a.blur + (b.blur - a.blur) * s), br: a.br + (b.br - a.br) * s, sc: a.sc + (b.sc - a.sc) * s,
});
const filt = (st: typeof REST) => `blur(${st.blur.toFixed(1)}px) brightness(${st.br.toFixed(3)})`;

const AlphaPlate: React.FC<{p: Plate; f: number; fps: number}> = ({p, f, fps}) => {
  const m = ALPHA_META[p.id] ?? {top: 0.05, bottom: 0.9, left: 0.1, right: 0.9, backing: 'none'};
  const hasLabels = LABELS.some((l) => l.plate === p.id);
  const TOP_SAFE = 0.06;                                           // art that starts at the very top (a headline at 2 %) is lowered to 6 % so the push never clips it
  const top0 = Math.max(m.top, TOP_SAFE), bottomT = hasLabels ? 0.76 : 0.84;
  const fit = Math.min(1, (bottomT - top0) / Math.max(0.05, m.bottom - m.top), bottomT / m.bottom);   // caption band starts at 85.5 % — 0.86 collided with it (audit, v21)
  const dropY = top0 - m.top;
  const pushMax = Math.min(PUSH, Math.max(0, (top0 - 0.02) / Math.max(0.05, fit * (m.bottom - m.top))));
  const layers = p.layers ?? null;
  const blurAts = (layers ?? []).filter((l) => l.mode === 'blur' && !l.sharp).map((l) => l.at).sort((a, b) => a - b);
  const card = m.backing === 'card' ? (
    <div style={{position: 'absolute', left: `${(m.left - 0.02) * 100}%`, top: `${(m.top - 0.03) * 100}%`, width: `${(m.right - m.left + 0.04) * 100}%`,
                 height: `${(m.bottom - m.top + 0.06) * 100}%`, background: CREAM, borderRadius: 28, boxShadow: '0 30px 60px rgba(0,0,0,0.45)'}} />
  ) : null;
  return (
    <Swing f={f} inAt={p.inFrame} out={p.outFrame} fps={fps}>
      {/* slow push across the plate's whole life (Yash 2026-10-05: a plate that sits still for 20 s is "less engaging") */}
      <div style={{position: 'absolute', inset: 0, transform: `translateY(${dropY * 100}%) scale(${fit})`, transformOrigin: `50% ${m.top * 100}%`}}>
      <div style={{position: 'absolute', inset: 0, transform: `scale(${1 + pushMax * Math.min(1, Math.max(0, (f - p.inFrame) / Math.max(1, p.outFrame - p.inFrame)))})`,
                   transformOrigin: `${((m.left + m.right) / 2) * 100}% ${m.bottom * 100}%`}}>{/* grows upward: the art bottom stays clear of the caption band */}
        {card}
        {!layers ? (
          <Img src={staticFile(p.asset)} style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
        ) : layers.map((l: Layer, i) => {
          const at = l.at;
          if (l.mode === 'blur') {
            // blurred at rest; sharp from its word; settles once a LATER blur element speaks
            let st = REST;
            if (l.sharp) st = ACTIVE;
            else if (f >= at) {
              st = lerpS(REST, ACTIVE, swingIn(f, at, fps));
              const later = blurAts.find((x) => x > at && x <= f);
              if (later !== undefined) st = lerpS(ACTIVE, SPOKEN, popIn(f, later, fps));
            }
            const b = l.bounds ?? [0, 0, 1, 1];
            return (
              <Img key={i} src={staticFile(l.asset)}
                   style={{position: 'absolute', inset: 0, width: '100%', height: '100%', filter: filt(st), transform: `scale(${st.sc})`,
                           transformOrigin: `${((b[0] + b[2]) / 2) * 100}% ${((b[1] + b[3]) / 2) * 100}%`}} />
            );
          }
          if (f < at) return null;                      // swing / chain: simply not mounted before its word
          return (
            <Swing key={i} f={f} inAt={at} out={p.outFrame} fps={fps} idle={false} exit={false} pop={l.mode === 'chain'}>
              <Img src={staticFile(l.asset)} style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
            </Swing>
          );
        })}
        <div data-audit={`plate:${p.id}`} style={{position: 'absolute', left: `${m.left * 100}%`, top: `${m.top * 100}%`,
             width: `${(m.right - m.left) * 100}%`, height: `${(m.bottom - m.top) * 100}%`}} />
      </div>
      </div>
    </Swing>
  );
};

/* ── GRID PLATE: N panels, all blurred + dimmed; each sharpens on its word; spoken ones settle to 80 %.
   Labels: baked into the image by default (grid.labels === 'baked'); Remotion chips only when 'remotion' — and then the DECLARED label text,
   never the join of the trigger words (that is how "निकास निकास" happened). ── */
const GridPlate: React.FC<{p: Plate; f: number; fps: number}> = ({p, f, fps}) => {
  const {rows, cols, panels, labels} = p.grid!;
  const trig = panels.map((pn) => pn.words.map((w) => wf(w, fps)).sort((a, b) => a - b));
  const all = trig.flatMap((t, i) => t.map((x) => [x, i] as [number, number])).sort((a, b) => a[0] - b[0]);
  return (
    <Swing f={f} inAt={p.inFrame} out={p.outFrame} fps={fps} idle={false}>
      <div data-audit={`grid:${p.id}`} style={{position: 'absolute', inset: 0}}>
        {panels.map((pn, i) => {
          const r = Math.floor(i / cols), c = i % cols;
          const mine = trig[i].filter((t) => t <= f);
          const a = mine.length ? mine[mine.length - 1] : -1;
          const later = all.filter(([t, j]) => j !== i && t > a && t <= f);
          const u = later.length ? later[0][0] : -1;
          let st = REST;
          if (a >= 0) {
            const before = mine.length > 1 ? SPOKEN : REST;
            st = lerpS(before, ACTIVE, swingIn(f, a, fps));
            if (u >= 0) st = lerpS(ACTIVE, SPOKEN, popIn(f, u, fps));
          }
          const isActive = a >= 0 && u < 0;
          const ls = a >= 0 ? popIn(f, a, fps) : 0;
          return (
            <div key={i} data-audit={`gridcell:${p.id}-${i}`}
                 style={{position: 'absolute', left: `${(c / cols) * 100}%`, top: `${(r / rows) * 100}%`, width: `${100 / cols}%`, height: `${100 / rows}%`,
                         overflow: 'hidden', transform: `scale(${st.sc})`, transformOrigin: '50% 50%', zIndex: isActive ? 3 : 1}}>
              <Img src={staticFile(p.asset)}
                   style={{position: 'absolute', left: `${-c * 100}%`, top: `${-r * 100}%`, width: `${cols * 100}%`, height: `${rows * 100}%`,
                           maxWidth: 'none', maxHeight: 'none', filter: filt(st)}} />
              {labels === 'remotion' && a >= 0 ? (
                <div style={{position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: '5%'}}>
                  <div data-audit={`label:${p.id}-${i}`}
                       style={{fontFamily: deva, fontSize: cols >= 5 ? 42 : 50, fontWeight: 800, color: isActive ? AMBER : TEXT, lineHeight: 1.3, textShadow: TEXT_SHADOW,
                               padding: '6px 22px', borderRadius: 12, background: SCRIM, textAlign: 'center',
                               transform: `translateY(${(1 - ls) * 40}px) scale(${0.7 + 0.3 * ls})`}}>
                    {pn.label}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Swing>
  );
};

/* ── FILM: full-frame H3 clip, hard cut. The clip is as long as its window by construction; the last-frame hold is only a safety net
   for a clip that landed short. ── */
const Film: React.FC<{p: Plate}> = ({p}) => {
  const clip = Math.min(p.clipFrames!, p.outFrame - p.inFrame);
  return (
    <div data-audit={`film:${p.id}`} style={{position: 'absolute', inset: 0}}>
      <Sequence from={p.inFrame} durationInFrames={clip} layout="none">
        <OffthreadVideo src={staticFile(p.asset)} muted style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
      </Sequence>
      {p.holdFrom! < p.outFrame ? (
        <Sequence from={p.holdFrom!} durationInFrames={p.outFrame - p.holdFrom!} layout="none">
          <Img src={staticFile(p.asset.replace('_film.mp4', '_last.png'))} style={{position: 'absolute', inset: 0, width: '100%', height: '100%'}} />
        </Sequence>
      ) : null}
    </div>
  );
};

/* ── words that land on their spoken time; the newest word is the one accent ── */
/* on-screen spelling of a spoken word: the TTS script writes ओम् (so the voice says it right); the screen shows ॐ (Yash 2026-10-05) */
const SHOW = (t: string) => t.replace(/^ओम्/, 'ॐ');
export const WordRow: React.FC<{words: number[]; f: number; fps: number; size: number; weight?: number; maxWidth?: string}> =
  ({words, f, fps, size, weight = 700, maxWidth = '84%'}) => {
    const mounted = words.filter((w) => wf(w, fps) <= f);
    if (!mounted.length) return null;
    const last = mounted[mounted.length - 1];
    return (
      <div style={{display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'baseline', columnGap: size * 0.38, rowGap: 6,
                   maxWidth, padding: `${size * 0.22}px ${size * 0.75}px`, borderRadius: 16, background: SCRIM, boxShadow: '0 10px 36px rgba(0,0,0,0.35)'}}>
        {mounted.map((w) => {
          const s = popIn(f, wf(w, fps), fps);
          return (
            <span key={w} style={{fontFamily: deva, fontSize: size, fontWeight: weight, lineHeight: 1.36, color: w === last ? AMBER : TEXT, textShadow: TEXT_SHADOW,
                                 display: 'inline-block', transform: `translateY(${(1 - s) * size * 0.55}px) scale(${0.72 + 0.28 * s})`}}>
              {SHOW(WORDS[w][2])}
            </span>
          );
        })}
      </div>
    );
  };

const TextBlock: React.FC<{t: Text; f: number; fps: number}> = ({t, f, fps}) => {
  const e = exitP(f, t.outFrame, 8);
  const cfg = t.kind === 'heading' ? {top: '36%', size: 96} : t.kind === 'oneliner' ? {top: '40%', size: 66} : {top: '85.5%', size: 50};
  return (
    <div data-audit={`${t.kind}:${t.id}`}
         style={{position: 'absolute', left: 0, right: 0, top: cfg.top, display: 'flex', justifyContent: 'center',
                 transform: `translateY(${e * (t.kind === 'caption' ? 260 : -900)}px)`}}>
      <WordRow words={t.words} f={f} fps={fps} size={cfg.size} weight={t.kind === 'heading' ? 800 : 700} />
    </div>
  );
};

/* ── label chips: DECLARED text, accumulate on their words, stay for the plate; films get a lower-third row ── */
const LabelRow: React.FC<{plate: Plate; f: number; fps: number}> = ({plate, f, fps}) => {
  const mine = LABELS.filter((l) => l.plate === plate.id && f >= l.inFrame && f < l.outFrame);
  if (!mine.length) return null;
  const e = exitP(f, plate.outFrame, 8);
  const film = plate.kind === 'film';
  return (
    <div style={{position: 'absolute', left: 0, right: 0, top: film ? '73%' : '77.5%', display: 'flex', justifyContent: 'center', columnGap: 22, transform: `translateY(${e * 260}px)`}}>
      {mine.map((l: Label) => {
        const s = popIn(f, l.inFrame, fps);
        const last = mine[mine.length - 1].id === l.id;
        return (
          <div key={l.id} data-audit={`label:${l.id}`}
               style={{fontFamily: deva, fontSize: film ? 60 : 46, fontWeight: 800, color: last ? AMBER : TEXT, lineHeight: 1.3, textShadow: TEXT_SHADOW,
                       padding: film ? '10px 34px' : '6px 24px', borderRadius: 14, background: SCRIM, transform: `translateY(${(1 - s) * 60}px) scale(${0.7 + 0.3 * s})`}}>
            {l.text}
          </div>
        );
      })}
    </div>
  );
};

/* ── cards (WhatsApp / end): logo + word lines from the timeline ── */
const Card: React.FC<{p: Plate; f: number; fps: number}> = ({p, f, fps}) => {
  const ls = swingIn(f, p.inFrame, fps);
  const lines = p.lines ?? [];
  return (
    <Swing f={f} inAt={p.inFrame} out={p.id === 'END' ? p.outFrame + 100 : p.outFrame} fps={fps} idle={false}>
      <div style={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', rowGap: 34}}>
        {p.logo !== false ? (
          <Img data-audit={`logo:${p.id}`} src={staticFile('logo/logo.png')}
               style={{height: 400, borderRadius: 22, transform: `scale(${0.8 + 0.2 * ls})`, boxShadow: '0 20px 60px rgba(0,0,0,0.45)'}} />
        ) : null}
        <div data-audit={`cardtext:${p.id}`} style={{display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 18}}>
          {lines.map((ln, i) => <WordRow key={i} words={ln} f={f} fps={fps} size={i === 0 ? 62 : 76} weight={800} />)}
        </div>
      </div>
    </Swing>
  );
};

/* ── layout auditor probe (audit_episode.mjs reads AUDIT:: from the console). Mounted ONLY when the audit passes inputProps {audit: true}:
   its delayRender() per frame timed out under CPU pressure during the v21 render (8 tabs on 4 cores) and flooded the logs. ── */
const AuditProbe: React.FC = () => {
  const frame = useCurrentFrame();
  React.useEffect(() => {
    const handle = delayRender('audit-measure');
    let tries = 0;
    const measure = () => {
      const imgs = Array.from(document.images);
      if (!imgs.every((i) => i.complete) && tries++ < 240) { requestAnimationFrame(measure); return; }
      const root = document.querySelector('[data-audit-root]') as HTMLElement | null;
      const rr = root ? root.getBoundingClientRect() : ({x: 0, y: 0} as DOMRect);
      const boxes = Array.from(document.querySelectorAll('[data-audit]')).map((e) => {
        const r = (e as HTMLElement).getBoundingClientRect();
        const st = getComputedStyle(e as HTMLElement);
        return {n: e.getAttribute('data-audit'), x: +(r.x - rr.x).toFixed(1), y: +(r.y - rr.y).toFixed(1), w: +r.width.toFixed(1), h: +r.height.toFixed(1),
                o: +(+st.opacity).toFixed(2), t: ((e as HTMLElement).innerText || '').slice(0, 22).replace(/\s+/g, ' ')};
      });
      // eslint-disable-next-line no-console
      console.log('AUDIT::' + JSON.stringify({frame, boxes}));
      continueRender(handle);
    };
    (document as any).fonts?.ready ? (document as any).fonts.ready.then(() => requestAnimationFrame(measure)) : requestAnimationFrame(measure);
  });
  return null;
};

export const EpisodeComp: React.FC<{audit?: boolean}> = ({audit = false}) => {
  const f = useStep();
  const {fps} = useVideoConfig();
  const plates = PLATES.filter((p) => f >= p.inFrame && f < p.outFrame);
  const texts = TEXTS.filter((t) => f >= t.inFrame && f < t.outFrame);
  return (
    <AbsoluteFill data-audit-root style={{backgroundColor: '#120C08', fontFamily: deva}}>
      <AbsoluteFill style={{zIndex: 0}}>
        <Grounds f={f} />
      </AbsoluteFill>
      <AbsoluteFill style={{zIndex: 10}}>
        {plates.map((p) => {
          if (p.kind === 'alpha') return <AlphaPlate key={p.id} p={p} f={f} fps={fps} />;
          if (p.kind === 'grid') return <GridPlate key={p.id} p={p} f={f} fps={fps} />;
          if (p.kind === 'film') return <Film key={p.id} p={p} />;
          if (p.kind === 'card') return <Card key={p.id} p={p} f={f} fps={fps} />;
          if (p.kind === 'custom') {
            const Cmp = CUSTOM[p.id];
            return Cmp ? <Cmp key={p.id} p={p} f={f} fps={fps} marks={MARKS[p.id] ?? {}} /> : null;
          }
          return null;
        })}
      </AbsoluteFill>
      <AbsoluteFill style={{zIndex: 20}}>
        {plates.map((p) => <LabelRow key={`l-${p.id}`} plate={p} f={f} fps={fps} />)}
        {texts.map((t) => <TextBlock key={t.id} t={t} f={f} fps={fps} />)}
      </AbsoluteFill>
      {audit ? <AuditProbe /> : null}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
